import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionsAPI } from '../services/api';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState({});
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    // Skip subscription check for superadmin, chain_admin, branch admins, waiter and kitchen
    if (!user || user.role === 'superadmin' || user.role === 'chain_admin' || user.role === 'waiter' || user.role === 'kitchen' || user.chain_id || !user.restaurant_id) {
      setLoading(false);
      return;
    }
    try {
      const res = await subscriptionsAPI.getMy();
      const data = res.data.data;
      setSubscription(data.subscription);
      setFeatures(data.subscription?.features || {});
      setPlanInfo(data.subscription);
    } catch {
      // No subscription yet
      setSubscription(null);
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  const isSubscriptionValid = (sub) => {
    if (!sub) return false;
    const now = new Date();
    if (sub.status === 'trial') {
      return sub.trial_ends_at && new Date(sub.trial_ends_at) > now;
    }
    if (sub.status === 'active' || sub.status === 'past_due') {
      return sub.current_period_end && new Date(sub.current_period_end) > now;
    }
    // cancelled with grace period: still valid until period end
    if (sub.status === 'cancelled' && sub.cancel_at_period_end) {
      return sub.current_period_end && new Date(sub.current_period_end) > now;
    }
    return false;
  };

  const hasFeature = (featureName) => {
    if (!user) return false;
    if (user.role === 'superadmin' || user.role === 'chain_admin' || user.role === 'waiter' || user.role === 'kitchen' || user.chain_id) return true;
    if (!subscription) return false;
    if (!isSubscriptionValid(subscription)) return false;
    return features[featureName] === true;
  };

  const isActive = () => {
    if (user?.role === 'superadmin' || user?.role === 'chain_admin' || user?.role === 'waiter' || user?.role === 'kitchen' || user?.chain_id) return true;
    if (!subscription) return false;
    return isSubscriptionValid(subscription);
  };

  const isTrial = () => subscription?.status === 'trial';

  const isCancelScheduled = () => !!(subscription?.cancel_at_period_end);

  const daysRemaining = () => {
    if (!subscription) return 0;
    const endDate = subscription.status === 'trial' ? subscription.trial_ends_at : subscription.current_period_end;
    if (!endDate) return 0;
    const diff = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  return (
    <SubscriptionContext.Provider value={{
      subscription, features, planInfo, loading,
      hasFeature, isActive, isTrial, isCancelScheduled, daysRemaining,
      fetchSubscription,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}
